// Considered: Atlas Node header row — rejected: welded to AtlasViewContext + columnsStyle. See spec Reuse Audit.
// Imports SphereRowMolecule.scss to share the single sphere-row-grid CSS grid-template definition.
import React from 'react';
import { useTranslation } from 'localization';
import './SphereRowMolecule.scss';
import './SphereRowHeaderMolecule.scss';

export type SortableCol = 'name' | 'description' | 'center';

export interface SphereRowHeaderMoleculeProps {
  /** Currently active sort column — null means unsorted */
  sortCol?: SortableCol | null;
  sortDir?: 'asc' | 'desc';
  /** Called when the user clicks a sortable column header */
  onSort?: (col: SortableCol) => void;
  className?: string;
}

function SortIcon({
  col,
  sortCol,
  sortDir,
}: {
  col: SortableCol;
  sortCol: SortableCol | null | undefined;
  sortDir: 'asc' | 'desc';
}) {
  const isActive = sortCol === col;
  if (isActive) {
    return (
      <i
        className={`pi ${sortDir === 'asc' ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down'} sphere-row-header-molecule__sort-icon sphere-row-header-molecule__sort-icon--active`}
        aria-hidden="true"
      />
    );
  }
  return (
    <i
      className="pi pi-sort-alt sphere-row-header-molecule__sort-icon"
      aria-hidden="true"
    />
  );
}

const SphereRowHeaderMolecule: React.FC<SphereRowHeaderMoleculeProps> = ({
  sortCol = null,
  sortDir = 'asc',
  onSort,
  className = '',
}) => {
  const { t } = useTranslation();

  const sortable = !!onSort;
  // The list below is a listbox of options, not a table, so these are sort CONTROLS for
  // it — plain buttons in a named group — and each states its own sort order in its name
  // (`aria-sort` exists only on table column headers, which this is not).
  const sortState = (col: SortableCol) =>
    sortCol === col ? (
      <span className="sphere-row-header-molecule__sort-state">
        {t(sortDir === 'asc' ? 'sphereRow.header.sortedAscending' : 'sphereRow.header.sortedDescending')}
      </span>
    ) : null;

  return (
    <fieldset className={`sphere-row-header-molecule sphere-row-grid ${className}`} aria-label={t('sphereRow.header.ariaLabel')}>
      {/* SPHERE — sortable */}
      <button
        type="button"
        className={`sphere-row-header-molecule__cell sphere-row-header-molecule__cell--btn${sortable ? ' sphere-row-header-molecule__cell--sortable' : ''}${sortCol === 'name' ? ' sphere-row-header-molecule__cell--sorted' : ''}`}
        onClick={sortable ? () => onSort('name') : undefined}
        disabled={!sortable}
      >
        {t('sphereRow.header.sphere')}
        {sortState('name')}
        {sortable && <SortIcon col="name" sortCol={sortCol} sortDir={sortDir} />}
      </button>

      {/* STATUS — not sortable */}
      <div
        className="sphere-row-header-molecule__cell sphere-row-header-molecule__cell--status"
      >
        {t('sphereRow.header.status')}
      </div>

      {/* DESCRIPTION — sortable */}
      <button
        type="button"
        className={`sphere-row-header-molecule__cell sphere-row-header-molecule__cell--btn${sortable ? ' sphere-row-header-molecule__cell--sortable' : ''}${sortCol === 'description' ? ' sphere-row-header-molecule__cell--sorted' : ''}`}
        onClick={sortable ? () => onSort('description') : undefined}
        disabled={!sortable}
      >
        {t('sphereRow.header.description')}
        {sortState('description')}
        {sortable && <SortIcon col="description" sortCol={sortCol} sortDir={sortDir} />}
      </button>

      {/* CENTER — sortable */}
      <button
        type="button"
        className={`sphere-row-header-molecule__cell sphere-row-header-molecule__cell--btn${sortable ? ' sphere-row-header-molecule__cell--sortable' : ''}${sortCol === 'center' ? ' sphere-row-header-molecule__cell--sorted' : ''}`}
        onClick={sortable ? () => onSort('center') : undefined}
        disabled={!sortable}
      >
        {t('sphereRow.header.center')}
        {sortState('center')}
        {sortable && <SortIcon col="center" sortCol={sortCol} sortDir={sortDir} />}
      </button>
    </fieldset>
  );
};

SphereRowHeaderMolecule.displayName = 'SphereRowHeaderMolecule';

export default SphereRowHeaderMolecule;
